"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { EditorContext } from "@/app/editor/_components/SharedImageScene";
import { useEditorHistory } from "@/hooks/useEditorHistory";
import { generateImageId } from "@/utils/imageUtils";
import { useCreditsContext } from "@/contexts/CreditsContext";
import InsufficientCreditsModal from "@/components/InsufficientCreditsModal";
import { addToGallery, getGalleryImages, clearGallerySession } from "@/libs/editor-image-storage";
import GallerySceneDevPanel from "@/components/dev/GallerySceneDevPanel";
import { isAiReportToolId } from "@/libs/ai-report-tools";
import { useEditorV2Health } from "@/libs/editor-v2-health-analytics";

// Global editor state provider
export default function GlobalEditorProvider({ children, toolInfo }: { children: React.ReactNode; toolInfo?: any }) {
  const historyHook = useEditorHistory();
  const { refreshCredits } = useCreditsContext();
  const router = useRouter();
  const health = useEditorV2Health();

  // Validate toolInfo is provided - this should never happen but fail loudly if it does
  if (!toolInfo) {
    throw new Error('[GlobalEditorProvider] toolInfo is required but was not provided');
  }

  // Validate creditCost exists
  if (typeof toolInfo.creditCost !== 'number') {
    throw new Error(`[GlobalEditorProvider] Invalid creditCost for tool ${toolInfo.id}: ${toolInfo.creditCost}`);
  }

  // Undo/redo history is only for the legacy chat refiner (dev). Edit with Chat uses the gallery.
  const isHistoryEnabled = toolInfo.id === 'ai-designer-dev';

  // Gallery = multi-result image tools. Report tools produce one analysis card and keep
  // their marketing pane until generate starts — gallery would treat the upload as a
  // "result" and swap the pane + enable Download too early.
  const isGalleryEnabled =
    !isHistoryEnabled &&
    !isAiReportToolId(toolInfo.id) &&
    (toolInfo.toolType === "editor" || toolInfo.toolType === "generator");

  // Generate session ID once per tool mount
  const sessionId = useRef(`session-${Date.now()}`);

  // Global editor state
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [processedImage, setProcessedImageState] = useState<string | null>(null);
  const [processedImageFileId, setProcessedImageFileId] = useState<string | null>(null); // Track fileId of processed image
  const [isProcessingState, setIsProcessingState] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // True while a source image upload/load is in flight
  const [savedImageIds, setSavedImageIds] = useState<Set<string>>(new Set()); // Track saved images in current session
  const [error, setError] = useState<string | null>(null);
  const [isLoadingRefineImage, setIsLoadingRefineImage] = useState(false);
  const [showInsufficientCreditsModalState, setShowInsufficientCreditsModalState] = useState(false);
  const [customDownloadHandler, setCustomDownloadHandler] = useState<(() => void) | undefined>(undefined);
  const [enhancementEnabled, setEnhancementEnabled] = useState(false); // Whether post-processing enhancement is enabled
  const processingPlaceholderId = useRef<string | null>(null);

  // Gallery state (only for dev tools)
  const [galleryImages, setGalleryImages] = useState<Array<{ imageId: string; imageData: string; timestamp: number; isProcessing?: boolean }>>([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  // Shared across all gallery tools so seed / generate / processing all open the result pane
  const [showResultPane, setShowResultPane] = useState(false);

  // Load gallery images on mount (only for dev tools)
  useEffect(() => {
    if (!isGalleryEnabled) return;

    async function loadGallery() {
      const images = await getGalleryImages(toolInfo.id, sessionId.current);
      setGalleryImages(images);
      // Set processedImage to first gallery image if exists
      if (images.length > 0) {
        setProcessedImageState(images[0].imageData);
      }
    }

    loadGallery();
  }, [isGalleryEnabled, toolInfo.id]);

  // Add original image to gallery when uploaded (only for dev tools)
  useEffect(() => {
    if (!isGalleryEnabled || !originalImage) return;

    // Check if original image already in gallery
    const originalImageId = generateImageId(originalImage);
    const alreadyExists = galleryImages.some(img => img.imageId === originalImageId);

    if (!alreadyExists) {
      setGalleryImages([{ imageId: originalImageId, imageData: originalImage, timestamp: Date.now() }]);
      setCurrentGalleryIndex(0);
    }
  }, [isGalleryEnabled, originalImage]);

  // Sync processedImage when gallery index changes (only for dev tools)
  useEffect(() => {
    if (!isGalleryEnabled || !galleryImages || galleryImages.length === 0) return;

    const currentImage = galleryImages[currentGalleryIndex];
    if (currentImage && !currentImage.isProcessing) {
      setProcessedImageState(currentImage.imageData);
    }
  }, [isGalleryEnabled, currentGalleryIndex, galleryImages]);

  // Load current image from history when component mounts or history changes (only for AI Designer)
  // For non-history tools, this effect will run but immediately return, preventing any state changes
  useEffect(() => {
    // CRITICAL: Only run this effect for history-enabled tools (AI Designer)
    // For all other tools, skip entirely to prevent any state interference
    if (!isHistoryEnabled) return;

    // Check for refineFileId in URL params (from "Refine with AI" button)
    const urlParams = new URLSearchParams(window.location.search);
    const refineFileId = urlParams.get('refineFileId');
    const fromTool = urlParams.get('fromTool');

    // Check if we already processed this refineFileId (prevent infinite loop)
    const lastProcessed = typeof window !== 'undefined' ? (window as any).__lastProcessedRefineFileId : null;

    if (refineFileId && refineFileId !== lastProcessed) {
      if (typeof window !== 'undefined') {
        (window as any).__lastProcessedRefineFileId = refineFileId;
      }
      setIsLoadingRefineImage(true);

      // CLEAR ALL PREVIOUS HISTORY FIRST
      historyHook.clearHistory();

      // Fetch the signed URL from API
      fetch(`/api/storage/signed-url/${refineFileId}`)
        .then(res => res.json())
        .then(data => {
          if (data.signedUrl) {
            const imageId = generateImageId(data.signedUrl);

            // Set as original image
            setOriginalImage(data.signedUrl);
            setProcessedImageState(null);
            (window as any).__uploadedFileId = refineFileId;

            // Add to history as FIRST action
            historyHook.trackPhotoUpload(data.signedUrl, imageId);

            // Clean URL (remove query params)
            router.replace('/editor/ai-designer', { scroll: false });
          }
          setIsLoadingRefineImage(false);
        })
        .catch(error => {
          console.error('Failed to load refinement image:', error);
          setIsLoadingRefineImage(false);
        });
    }

    const currentAction = historyHook.history.actions[historyHook.history.currentIndex];
    if (currentAction) {
      if (currentAction.type === 'photo_uploaded') {
        setOriginalImage(currentAction.imageData);
        setProcessedImageState(null);
      } else if (currentAction.type === 'photo_updated') {
        // Set both original (from first action) and processed (current action)
        const firstAction = historyHook.history.actions[0];
        if (firstAction && firstAction.type === 'photo_uploaded') {
          setOriginalImage(firstAction.imageData);
        }
        setProcessedImageState(currentAction.imageData);
      }
    } else {
      // No history - reset to blank state (only for AI Designer)
      setOriginalImage(null);
      setProcessedImageState(null);
    }
  }, [isHistoryEnabled, historyHook.history.currentIndex, historyHook.history.actions]);

  // Custom setIsProcessing for gallery mode
  const setIsProcessing = (processing: boolean) => {
    setIsProcessingState(processing);
    if (processing) {
      setShowResultPane(true);
      health?.markGenerateStarted();
    }

    // Scroll to top on mobile when processing starts
    if (processing && typeof window !== 'undefined') {
      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        // Use smooth scroll for better UX
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Ensure we reach the top by checking after animation
        setTimeout(() => {
          if (window.scrollY > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 300);
      }
    }

    // Gallery mode: Add processing placeholder when starting
    if (isGalleryEnabled && processing) {
      const placeholderId = `processing-${Date.now()}`;
      processingPlaceholderId.current = placeholderId;

      // Prepend processing placeholder
      setGalleryImages(prev => [
        { imageId: placeholderId, imageData: '', timestamp: Date.now(), isProcessing: true },
        ...prev
      ]);

      // Set as active
      setCurrentGalleryIndex(0);
    }
  };

  // Enhanced setProcessedImage that automatically tracks history, manages processing state, and refreshes credits
  const setProcessedImage = async (imageData: string | null, fileId?: string) => {
    // Preload remote images before swapping UI — avoids empty/flicker frames.
    if (imageData && imageData.startsWith("http")) {
      try {
        await new Promise<void>((resolve, reject) => {
          const img = new window.Image();
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("Failed to load image"));
          img.src = imageData;
        });
      } catch (error) {
        console.error("Failed to preload image:", error);
        // Continue anyway — overlay stays until we commit state below
      }
    }

    if (imageData) {
      setShowResultPane(true);
      if (fileId) {
        (window as any).__lastGeneratedFileId = fileId;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("refreshAssets"));
        }
      }
      health?.track("result_shown", {
        duration_ms: health.generateDurationMs(),
      });
    }

    // Gallery mode: Store fileIds, display URLs
    if (imageData && isGalleryEnabled) {
      const imageId = fileId || generateImageId(imageData);

      addToGallery(toolInfo.id, sessionId.current, imageData, imageId);

      const hasPlaceholder = processingPlaceholderId.current !== null;
      const placeholderToReplace = processingPlaceholderId.current;
      processingPlaceholderId.current = null;

      setGalleryImages((prev) => {
        if (hasPlaceholder) {
          return prev.map((img) =>
            img.imageId === placeholderToReplace
              ? { imageId, imageData, timestamp: Date.now() }
              : img
          );
        }
        return prev;
      });

      setCurrentGalleryIndex(0);
      setProcessedImageState(imageData);
      // Clear processing only after the result slide is committed (same batch as image)
      setIsProcessingState(false);
      refreshCredits();
      return;
    }

    // Non-gallery: commit result BEFORE clearing processing so the overlay
    // never exposes an empty/stale frame (matches gallery placeholder behavior).
    setProcessedImageState(imageData);

    if (imageData && fileId) {
      setProcessedImageFileId(fileId);
    } else if (!imageData) {
      setProcessedImageFileId(null);
      setShowResultPane(false);
    }

    if (imageData) {
      setIsProcessingState(false);
      refreshCredits();
    }

    // Only track history for AI Designer/Refiner tool - other tools no longer use history
    if (imageData && toolInfo && isHistoryEnabled) {
      const imageId = generateImageId(imageData);
      historyHook.trackPhotoUpdate(imageData, toolInfo, imageId);
    }
  };

  // Non-tracking version for internal use (like undo/redo) - no credit refresh needed
  const setProcessedImageInternal = (imageData: string | null) => {
    setProcessedImageState(imageData);

    // Handle processing overlay but no credit refresh for history navigation
    if (imageData) {
      setIsProcessingState(false); // Use state setter directly to avoid gallery placeholder logic
      setShowResultPane(true);
    } else {
      setShowResultPane(false);
    }
  };

  // Enhanced error handling that automatically manages processing state and handles insufficient credits
  const setErrorAndStopProcessing = (errorMessage: string | null) => {
    // Check if this is an insufficient credits error
    if (errorMessage === "Insufficient credits") {
      // Show the modal instead of setting the error
      setShowInsufficientCreditsModalState(true);
      setIsProcessing(false);
      // Remove processing placeholder if in gallery mode
      if (isGalleryEnabled && processingPlaceholderId.current) {
        setGalleryImages(prev => prev.filter(img => img.imageId !== processingPlaceholderId.current));
        processingPlaceholderId.current = null;
      }
      // Don't set the error message since we're showing a modal
      return;
    }

    if (errorMessage) {
      // Remove processing placeholder SYNCHRONOUSLY
      if (isGalleryEnabled && processingPlaceholderId.current) {
        const placeholderToRemove = processingPlaceholderId.current;
        processingPlaceholderId.current = null;

        setGalleryImages(prev => prev.filter(img => img.imageId !== placeholderToRemove));
      }

      // Stop processing and set error in one batch
      setIsProcessing(false);
      setError(errorMessage);
    } else {
      setError(null);
    }
  };

  // Placeholder - will be overridden by tool config
  const handleGenerate = async () => {
    console.log("Generate should be implemented by tool config");
  };

  // Track photo updates in history (legacy method, now redundant but kept for compatibility)
  const trackPhotoUpdate = (imageData: string) => {
    // This is now handled by tools calling historyHook.trackPhotoUpdate directly
  };

  // Clear gallery (for dev tools)
  const clearGallery = async () => {
    if (!isGalleryEnabled) return;

    await clearGallerySession(toolInfo.id, sessionId.current);
    setGalleryImages([]);
    setCurrentGalleryIndex(0);
    setProcessedImageState(null); // Clear processedImage for system consistency
    setShowResultPane(false);
  };


  const editorContextValue = {
    originalImage,
    setOriginalImage,
    processedImage,
    setProcessedImage,
    setProcessedImageInternal,
    isProcessing: isProcessingState || isLoadingRefineImage, // Include refine loading state
    setIsProcessing,
    isUploading,
    setIsUploading,
    error,
    setError: setErrorAndStopProcessing, // Use enhanced error setter
    handleGenerate,
    getCurrentImage: () => {
      // AI Designer is the only tool that builds on previous changes
      if (toolInfo.id === 'ai-designer') {
        return processedImage || originalImage;
      }
      // Gallery mode (dev tools): ALWAYS use original image (each generation is independent)
      // ALL other tools should ALWAYS use the original image to prevent quality degradation
      return originalImage;
    },
    hasProcessedImage: () => {
      if (isGalleryEnabled) {
        return galleryImages.length > 0;
      }
      return !!processedImage;
    },
    trackPhotoUpdate,
    isHistoryEnabled, // Pass through history enabled flag
    isLoadingRefineImage, // Pass the loading state
    customDownloadHandler,
    setCustomDownloadHandler,

    // Gallery state (for dev tools)
    galleryImages,
    currentGalleryIndex,
    setCurrentGalleryIndex,
    isGalleryEnabled,
    clearGallery,
    showResultPane,
    setShowResultPane,

    // Processed image fileId (for save button in non-gallery mode)
    processedImageFileId,

    // Saved images tracking (in-memory for current session)
    savedImageIds,
    setSavedImageIds,

    // Enhancement state (for tools with optional post-processing)
    enhancementEnabled,
    setEnhancementEnabled,
  };

  return (
    <EditorContext.Provider value={editorContextValue}>
      {children}

      {process.env.NODE_ENV === "development" && <GallerySceneDevPanel />}

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModalState}
        onClose={() => setShowInsufficientCreditsModalState(false)}
        creditsRequired={toolInfo.creditCost}
        toolName={toolInfo.title}
      />
    </EditorContext.Provider>
  );
}
